import { Component, ViewChild, ElementRef, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { FormatService } from '../../format.service';
import { InvestmentChartService } from '../../investment-chart.service';
import { validateRange, validateRangeAllowZero } from '../../range-validate';
import {
  TdsPropertyCalculatorService, TdsPropertyResult, TDS_PROPERTY_CONSTRAINTS,
  INDIAN_STATE_STAMP_DUTY, SellerResidency, HoldingPeriod
} from './tds-property-sale-calculator.service';

@Component({
  selector: 'app-tds-property-sale-calculator',
  standalone: true,
  imports: [FormsModule, DatePipe],
  providers: [InvestmentChartService],
  templateUrl: './tds-property-sale-calculator.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tds-property-sale-calculator.scss']
})
export class TdsPropertySaleCalculator implements OnDestroy {
  fmt = inject(FormatService);
  private calc = inject(TdsPropertyCalculatorService);
  private charts = inject(InvestmentChartService);
  private cdr = inject(ChangeDetectorRef);


  @ViewChild('breakdownRef') breakdownRef!: ElementRef<HTMLCanvasElement>;

  readonly C = TDS_PROPERTY_CONSTRAINTS;
  readonly states = INDIAN_STATE_STAMP_DUTY;

  residency:         SellerResidency = 'resident';
  state = 'Maharashtra';
  saleConsideration = 6_000_000;
  stampDutyValue = 0;
  panAvailable = true;
  holdingPeriod:     HoldingPeriod = 'longTerm';
  dateOfDeduction:   string = new Date().toISOString().slice(0, 10);
  dateOfDeposit:     string = new Date().toISOString().slice(0, 10);

  formErrors:    Record<string, string>  = {};
  touched:       Record<string, boolean> = {};
  showFormErrors = false;

  calculated = false;
  result:     TdsPropertyResult | null = null;

  private validateForm(): boolean {
    const errors: Record<string, string> = {};
    const e1 = validateRange(this.saleConsideration, this.C.saleConsideration.min, this.C.saleConsideration.max, 'Sale consideration');
    if (e1) errors['saleConsideration'] = e1;
    const e2 = validateRangeAllowZero(this.stampDutyValue, this.C.stampDutyValue.min, this.C.stampDutyValue.max, 'Stamp duty value');
    if (e2) errors['stampDutyValue'] = e2;
    this.formErrors = errors;
    return Object.keys(errors).length === 0;
  }

  get isFormValid(): boolean { return this.validateForm(); }

  setResidency(residency: SellerResidency): void {
    this.residency = residency;
    this.resetCalculatedState();
  }

  setPanAvailable(available: boolean): void {
    this.panAvailable = available;
    this.resetCalculatedState();
  }

  setHoldingPeriod(period: HoldingPeriod): void {
    this.holdingPeriod = period;
    this.resetCalculatedState();
  }

  onFieldChange(field: string): void {
    this.touched[field] = true;
    this.validateForm();
    this.resetCalculatedState();
  }

  onFieldBlur(field: string): void {
    this.touched[field] = true;
    this.validateForm();
  }

  fieldError(field: string): string | null {
    return (this.touched[field] || this.showFormErrors) && this.formErrors[field]
      ? this.formErrors[field]
      : null;
  }

  hasError(field: string): boolean { return !!this.fieldError(field); }

  private resetCalculatedState(): void {
    if (this.calculated) {
      this.calculated = false;
      this.charts.destroyAll();
    }
  }

  calculate(): void {
    this.showFormErrors = true;
    this.touched = { saleConsideration: true, stampDutyValue: true };
    if (!this.validateForm()) return;

    this.result = this.calc.calculate({
      residency: this.residency,
      state: this.state,
      saleConsideration: this.saleConsideration,
      stampDutyValue: this.stampDutyValue,
      panAvailable: this.panAvailable,
      holdingPeriod: this.holdingPeriod,
      dateOfDeduction: this.dateOfDeduction,
      dateOfDeposit: this.dateOfDeposit
    });
    this.calculated = true;
    this.cdr.detectChanges();

    setTimeout(() => {
      const r = this.result!;
      this.charts.renderBreakdownBar(this.breakdownRef.nativeElement, [
        { label: 'TDS Deducted', value: r.tdsAmount, color: '#ef4444' },
        { label: 'Net to Seller', value: r.netAmountToSeller, color: '#10b981' }
      ]);
    }, 60);
  }

  reset(): void {
    this.residency         = 'resident';
    this.state              = 'Maharashtra';
    this.saleConsideration = 6_000_000;
    this.stampDutyValue    = 0;
    this.panAvailable      = true;
    this.holdingPeriod     = 'longTerm';
    this.dateOfDeduction   = new Date().toISOString().slice(0, 10);
    this.dateOfDeposit     = new Date().toISOString().slice(0, 10);
    this.formErrors        = {};
    this.touched           = {};
    this.showFormErrors    = false;
    this.calculated        = false;
    this.result            = null;
    this.charts.destroyAll();
  }

  ngOnDestroy(): void {
    this.charts.destroyAll();
  }
}
